import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Semaforo, StatusBadge , EstadoBadge } from "../components/Semaforo";
import { formatDate, formatLabel } from "../lib/format";
import {
  Users, Buildings, Briefcase, ListChecks, CurrencyCircleDollar, Heartbeat,
  WarningCircle, ChartLineUp, Leaf, Calendar
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, CartesianGrid, Legend
} from "recharts";

const PIE_COLORS = ["#85c536", "#F59E0B", "#EF4444"];

const formatCLP = (n) =>
  new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);

function KPICard({ icon: Icon, label, value, hint, testid, accent = "slate" }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-medium">{label}</div>
        <Icon size={18} className={`text-${accent}-600`} weight="regular" />
      </div>
      <div className="mt-4 font-display text-3xl font-bold text-mono text-slate-900">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="p-8 text-slate-500">Cargando dashboard…</div>
    );
  }

  const { kpis, semaforo, projects_by_estado, inversion_por_eje, proximos_vencimientos, alertas_criticas, inversion_por_comunidad } = data;
  const semaforoData = [
    { name: "Verde", value: semaforo.verde },
    { name: "Amarillo", value: semaforo.amarillo },
    { name: "Rojo", value: semaforo.rojo },
  ];

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Dashboard ejecutivo"
        description="Visión consolidada de comunidades, autoridades, proyectos, compromisos e inversión social y ambiental."
      />

      <div className="px-6 py-6 space-y-6">
        {/* KPI Row */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <KPICard testid="kpi-communities" icon={Users} label="Comunidades" value={kpis.communities} hint="Gestionadas" />
          <KPICard testid="kpi-authorities" icon={Buildings} label="Autoridades" value={kpis.authorities} hint="Registradas" />
          <KPICard testid="kpi-projects" icon={Briefcase} label="Proyectos activos" value={kpis.projects_active} />
          <KPICard testid="kpi-rca" icon={Leaf} label="Compromisos RCA" value={kpis.commitments_rca} accent="emerald" />
          <KPICard testid="kpi-op" icon={ListChecks} label="Compromisos op." value={kpis.commitments_op} />
          <KPICard testid="kpi-vencidos" icon={WarningCircle} label="Vencidos" value={kpis.commitments_vencidos} accent="red" />
        </div>

        {/* Investment row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KPICard testid="kpi-inv-total" icon={CurrencyCircleDollar} label="Inversión total" value={formatCLP(kpis.inversion_total)} hint="Presupuesto proyectos" />
          <KPICard testid="kpi-inv-ejecutada" icon={ChartLineUp} label="Inv. ejecutada" value={formatCLP(kpis.inversion_ejecutada)} accent="emerald" />
          <KPICard testid="kpi-benef-directos" icon={Heartbeat} label="Beneficiarios directos" value={kpis.beneficiarios_directos} />
          <KPICard testid="kpi-benef-indirectos" icon={Heartbeat} label="Beneficiarios indirectos" value={kpis.beneficiarios_indirectos} />
        </div>

        {/* Charts + lists */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Semáforo */}
          <Card className="rounded-md shadow-sm">
            <CardHeader>
              <CardTitle className="font-display text-base">Semáforo de compromisos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={semaforoData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={75} paddingAngle={2}>
                      {semaforoData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={24} iconType="circle" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-center text-xs">
                <div><div className="font-display text-xl">{semaforo.verde}</div><div className="text-brand-green-700">Vigente</div></div>
                <div><div className="font-display text-xl">{semaforo.amarillo}</div><div className="text-amber-700">Próximo</div></div>
                <div><div className="font-display text-xl">{semaforo.rojo}</div><div className="text-red-700">Crítico</div></div>
              </div>
            </CardContent>
          </Card>

          {/* Inversión por eje */}
          <Card className="rounded-md shadow-sm lg:col-span-2">
            <CardHeader>
              <CardTitle className="font-display text-base">Inversión por eje estratégico</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={inversion_por_eje}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="eje" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1e6 ? `${(v/1e6).toFixed(0)}M` : v} />
                    <Tooltip formatter={(v) => formatCLP(v)} />
                    <Bar dataKey="total" fill="#ff751f" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tables: alertas críticas + próximos vencimientos */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="rounded-md shadow-sm" data-testid="card-alertas-criticas">
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="font-display text-base flex items-center gap-2">
                <WarningCircle weight="duotone" className="text-red-600" /> Alertas críticas
              </CardTitle>
              <Link to="/alertas" className="text-xs text-slate-600 hover:text-slate-900">Ver todas →</Link>
            </CardHeader>
            <CardContent className="p-0">
              {alertas_criticas.length === 0 ? (
                <div className="p-6 text-sm text-slate-500">Sin alertas críticas.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {alertas_criticas.slice(0,8).map((a) => (
                    <li key={a.id} className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{a.name}</div>
                        <div className="text-xs text-slate-500">{a.responsable_interno || "—"}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">{formatDate(a.fecha_compromiso)}</span>
                        <Semaforo value="rojo" />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-md shadow-sm" data-testid="card-proximos">
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="font-display text-base flex items-center gap-2">
                <Calendar weight="duotone" /> Próximos vencimientos
              </CardTitle>
              <Link to="/compromisos" className="text-xs text-slate-600 hover:text-slate-900">Ver todos →</Link>
            </CardHeader>
            <CardContent className="p-0">
              {proximos_vencimientos.length === 0 ? (
                <div className="p-6 text-sm text-slate-500">Sin vencimientos próximos.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {proximos_vencimientos.slice(0,8).map((c) => (
                    <li key={c.id} className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{c.name}</div>
                        <div className="text-xs text-slate-500">{formatLabel(c.tipo)} · {c.responsable_interno || "—"}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">{formatDate(c.fecha_compromiso)}</span>
                        <Semaforo value={c.semaforo} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Inversión por comunidad + projects estado */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="rounded-md shadow-sm">
            <CardHeader>
              <CardTitle className="font-display text-base">Inversión por comunidad (top 10)</CardTitle>
            </CardHeader>
            <CardContent>
              {inversion_por_comunidad.length === 0 ? (
                <div className="text-sm text-slate-500">Sin datos.</div>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer>
                    <BarChart data={inversion_por_comunidad} layout="vertical" margin={{ left: 16 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1e6 ? `${(v/1e6).toFixed(0)}M` : v} />
                      <YAxis dataKey="comunidad" type="category" tick={{ fontSize: 11 }} width={120} />
                      <Tooltip formatter={(v) => formatCLP(v)} />
                      <Bar dataKey="presupuesto" fill="#3a3939" />
                      <Bar dataKey="ejecutada" fill="#85c536" />
                      <Legend />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-md shadow-sm">
            <CardHeader>
              <CardTitle className="font-display text-base">Proyectos por estado</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="divide-y divide-slate-100">
                {projects_by_estado.length === 0 && (
                  <li className="text-sm text-slate-500 py-4">Sin proyectos registrados.</li>
                )}
                {projects_by_estado.map((p) => (
                  <li key={p.estado} className="py-2.5 flex items-center justify-between text-sm">
                    <EstadoBadge value={p.estado} />
                    <span className="font-mono font-semibold">{p.n}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
